import { WallType } from '../types/floorplannerTypes';
import type { FloorplannerStore } from '../store/floorplannerStore';

// Lazy accessor to avoid circular dependency with floorplannerStore
let _floorplannerStore: FloorplannerStore | null = null;
const getFloorplannerStore = (): FloorplannerStore => {
  if (!_floorplannerStore) {
    const globalStore = (globalThis as typeof globalThis & { __boniFloorplannerStore?: FloorplannerStore }).__boniFloorplannerStore;
    if (globalStore) {
      _floorplannerStore = globalStore;
    } else {
      throw new Error('FloorplannerStore not yet initialized - wallVersioning called too early');
    }
  }
  return _floorplannerStore;
};

/**
 * Increment the geometry version of a wall when its geometry changes.
 * This includes position, width, connections, or any property that affects rendering.
 */
export function incrementWallVersion(wall: WallType): void {
  wall.geometryVersion = (wall.geometryVersion || 0) + 1;
  wall.lastUpdate = Date.now();
}

/**
 * Increment versions of all connected walls when a wall changes.
 * This ensures that connected walls know they need to rebuild their geometry
 * to trim correctly against the changed wall.
 */
export function incrementConnectedWallVersions(wallId: string): void {
  const wall = getFloorplannerStore().wallsMap.get(wallId);
  if (!wall || !wall.centerConnections) return;

  for (const connection of wall.centerConnections) {
    const connectedWall = getFloorplannerStore().wallsMap.get(connection.id);
    if (connectedWall) {
      incrementWallVersion(connectedWall);
    }
  }
}

/**
 * Check if a wall's geometry needs to be rebuilt based on version numbers.
 * Returns true if:
 * 1. Wall has no geometry version (never built)
 * 2. Any connected wall has a newer version than when this wall was last built
 */
export function wallNeedsGeometryRebuild(
  wallId: string,
  cachedGeometryVersion?: number,
  cachedDependencyVersions?: Map<string, number>
): boolean {
  const wall = getFloorplannerStore().wallsMap.get(wallId);
  if (!wall) return false;

  // If wall has no version, it needs to be built
  const currentVersion = wall.geometryVersion || 0;
  if (currentVersion === 0) return true;

  // If no cached geometry version, needs rebuild
  if (cachedGeometryVersion === undefined) return true;

  // If wall's own version has changed since last cache, needs rebuild
  if (currentVersion !== cachedGeometryVersion) return true;

  // Check if any connected wall's version has changed
  if (wall.centerConnections && cachedDependencyVersions) {
    for (const connection of wall.centerConnections) {
      const connectedWall = getFloorplannerStore().wallsMap.get(connection.id);
      if (connectedWall) {
        const connectedVersion = connectedWall.geometryVersion || 0;
        const cachedConnectedVersion = cachedDependencyVersions.get(connection.id);

        // If we don't have a cached version for this connection, needs rebuild
        if (cachedConnectedVersion === undefined) return true;

        // If connected wall version has changed, needs rebuild
        if (connectedVersion !== cachedConnectedVersion) return true;
      }
    }
  }

  return false;
}

/**
 * Initialize geometry version for walls that don't have one yet.
 * This ensures backward compatibility with existing floor plans.
 */
export function ensureWallHasVersion(wall: WallType): void {
  if (wall.geometryVersion === undefined) {
    wall.geometryVersion = 1;
  }
}
